import React, { useMemo } from 'react';
import styles from './TDGLinkCard.module.css';
import { addAnalyticsParam } from '../../utils/analytics';
import { logger } from '../../utils/logger';

interface Vehicle {
  description?: string;
  tireSize?: string;
  frontTireSize?: string;
  rearTireSize?: string;
  season?: string;
  isOffRoad?: boolean;
  isRunflat?: boolean;
}

interface Indicator {
  type: string;
  label: string;
  description?: string;
}

interface TDGPart {
  title?: string;
  tdgUrl: string;
  vehicle?: Vehicle;
  indicators?: Indicator[];
  ctaText?: string;
}

interface TDGLinkCardProps {
  part: TDGPart;
  onChipSelect?: (message: string, chipType: string) => void;
  isInteractive?: boolean;
  onUrlChange?: (url: string) => void;
  // TRAC-527: Turn number for data-track-turn attributes (mirrors
  // effectiveTurnNumber in MessageRenderer — -1 for client-only).
  turnNumber: number;
}

type SeasonType = 'allyear' | 'summerOnly' | 'winterOnly';

type AttributeKey = 'wethandling' | 'dryhandling' | 'ridecomfort' | 'wear' | 'price' | 'wintertraction' | 'icetraction' | 'snowtraction' | 'trailtraction';

/**
 * TDGLinkCard - Interactive Tire Decision Guide card with priority chips
 *
 * Displays vehicle information and clickable chips that send messages
 * to the agent to adjust recommendations.
 *
 * Priority System:
 * - Chip states: Disabled (0) ↔ Enabled (4)
 * - Incoming URL values >= 3 are converted to 4 (enabled)
 * - Incoming URL values < 3 are converted to 0 (disabled)
 *
 * Attribute mapping by seasonType:
 * - allyear: wethandling, wintertraction, dryhandling, ridecomfort, wear, price, trailtraction (if offRoad=Y)
 * - summerOnly: wethandling, dryhandling, ridecomfort, wear, price, trailtraction (if offRoad=Y)
 * - winterOnly: icetraction, snowtraction, wethandling, ridecomfort, dryhandling, wear, price
 */
const TDGLinkCard: React.FC<TDGLinkCardProps> = ({ part, onChipSelect, isInteractive = true, onUrlChange, turnNumber }) => {
  // Track modified priority values (only store values that differ from original URL)
  const [modifiedPriorities, setModifiedPriorities] = React.useState<Record<string, number>>({});

  // Track if user has made any modifications
  const hasModifications = Object.keys(modifiedPriorities).length > 0;

  // Generate customized URL based on modifications
  const customizedUrl = useMemo(() => {
    if (!hasModifications) return part.tdgUrl;

    try {
      const url = new URL(part.tdgUrl);
      // Apply all modifications. Drop zero-valued attribute weights — TDG can
      // mishandle `param=0`, so treat "toggled off" as param-absent.
      Object.entries(modifiedPriorities).forEach(([key, value]) => {
        if (value === 0) {
          url.searchParams.delete(key);
        } else {
          url.searchParams.set(key, String(value));
        }
      });
      const newUrl = url.toString();
      logger.debug('Generated customized URL:', newUrl);
      return newUrl;
    } catch (e) {
      logger.error('Failed to generate customized URL:', e);
      return part.tdgUrl;
    }
  }, [part.tdgUrl, modifiedPriorities, hasModifications]);

  // Notify parent when URL changes
  React.useEffect(() => {
    if (onUrlChange) {
      onUrlChange(customizedUrl);
    }
  }, [customizedUrl, onUrlChange]);

  // Parse URL to extract all parameters
  const urlParams = useMemo(() => {
    try {
      const url = new URL(part.tdgUrl);
      const params: Record<string, string> = {};
      url.searchParams.forEach((value, key) => {
        params[key] = value;
      });
      return params;
    } catch (e) {
      logger.error('Failed to parse TDG URL:', e);
      return {};
    }
  }, [part.tdgUrl]);

  // Determine current season type and off-road mode (use modified value if it exists)
  const seasonType = (modifiedPriorities.seasonType as SeasonType) || (urlParams.seasonType as SeasonType) || 'allyear';
  const isOffRoadMode = modifiedPriorities.offRoad === 'Y' || (modifiedPriorities.offRoad === undefined && urlParams.offRoad === 'Y');
  const isRunflatMode = modifiedPriorities.runflat === 'Y' || (modifiedPriorities.runflat === undefined && urlParams.runflat === 'Y');

  // Attribute display labels
  const attributeLabels: Record<AttributeKey, string> = {
    wethandling: 'Wet Handling',
    dryhandling: 'Dry Handling',
    ridecomfort: 'Ride Comfort',
    wear: 'Treadwear',
    price: 'Price',
    wintertraction: 'Winter Traction',
    icetraction: 'Ice Traction',
    snowtraction: 'Snow Traction',
    trailtraction: 'Trail Traction'
  };

  // Get attributes to display based on seasonType - memoized to prevent unnecessary recalculations
  const visibleAttributes = useMemo((): AttributeKey[] => {
    const baseAttrs: AttributeKey[] = ['wethandling', 'dryhandling', 'ridecomfort', 'wear', 'price'];

    if (seasonType === 'winterOnly') {
      // winterOnly: icetraction, snowtraction + base
      return ['icetraction', 'snowtraction', ...baseAttrs];
    } else if (seasonType === 'allyear') {
      // allyear: base + wintertraction + trailtraction (if offRoad)
      const attrs: AttributeKey[] = [...baseAttrs, 'wintertraction'];
      if (isOffRoadMode) {
        attrs.push('trailtraction');
      }
      return attrs;
    } else {
      // summerOnly: base + trailtraction (if offRoad)
      const attrs: AttributeKey[] = [...baseAttrs];
      if (isOffRoadMode) {
        attrs.push('trailtraction');
      }
      return attrs;
    }
  }, [seasonType, isOffRoadMode]);

  // Get current high priorities (value >= 3) for display - from original URL only
  const currentHighPriorities = useMemo(() => {
    const priorities: string[] = [];
    visibleAttributes.forEach(attr => {
      const value = parseInt(urlParams[attr] || '0', 10);
      if (value >= 3) {
        priorities.push(attributeLabels[attr]);
      }
    });
    return priorities;
  }, [urlParams, visibleAttributes]);

  // Get priority value for an attribute (use modified value if it exists, otherwise use URL param)
  // Converts incoming values: >= 3 → 4 (enabled), < 3 → 0 (disabled)
  const getPriorityValue = (attr: AttributeKey): number => {
    if (modifiedPriorities[attr] !== undefined) {
      return modifiedPriorities[attr];
    }
    const urlValue = parseInt(urlParams[attr] || '0', 10);
    // Convert incoming URL values to binary system
    return urlValue >= 3 ? 4 : 0;
  };

  // Toggle priority value: 0 ↔ 4 (disabled ↔ enabled)
  const togglePriorityValue = (currentValue: number): number => {
    return currentValue === 0 ? 4 : 0;
  };

  // Handle priority chip click - toggle between 0 (disabled) and 4 (enabled)
  const handleChipClick = (attr: AttributeKey) => {
    if (!isInteractive) return;

    const currentValue = getPriorityValue(attr);
    const newValue = togglePriorityValue(currentValue);

    logger.debug(`Priority toggle: ${attr} ${currentValue} → ${newValue}`);

    // TRAC-527: tracking emitted by ChatTrackingRoot via data-track-* attrs

    setModifiedPriorities(prev => ({
      ...prev,
      [attr]: newValue
    }));
  };

  // Handle season type change (radio button style - mutually exclusive)
  const handleSeasonTypeClick = (newSeasonType: SeasonType) => {
    if (!isInteractive) return;

    logger.debug(`Season type change: ${seasonType} → ${newSeasonType}`);

    setModifiedPriorities(prev => ({
      ...prev,
      seasonType: newSeasonType
    }));
  };

  // Handle off-road toggle
  const handleOffRoadToggle = () => {
    if (!isInteractive) return;

    const newOffRoadValue = isOffRoadMode ? 'N' : 'Y';
    logger.debug(`Off-road toggle: ${isOffRoadMode ? 'Y' : 'N'} → ${newOffRoadValue}`);

    setModifiedPriorities(prev => ({
      ...prev,
      offRoad: newOffRoadValue
    }));
  };

  // Handle runflat toggle (TRAC-285)
  const handleRunflatToggle = () => {
    if (!isInteractive) return;

    const newRunflatValue = isRunflatMode ? 'N' : 'Y';

    setModifiedPriorities(prev => ({
      ...prev,
      runflat: newRunflatValue
    }));
  };

  // Get display text for season
  const getSeasonDisplay = (): string => {
    if (seasonType === 'winterOnly') return 'Winter';
    if (seasonType === 'summerOnly') return 'Summer';
    return 'All-Season';
  };

  return (
    <div className={styles.tdgLinkCardPart}>
      {part.title && <div className={styles.tdgCardTitle}>{part.title}</div>}
      <div
        className={styles.tdgCard}
        data-track-id="tdg_link"
        data-track-turn={turnNumber}
      >
        {/* Vehicle Info */}
        {part.vehicle && (
          <div className={styles.tdgVehicleInfo}>
            <div className={styles.tdgVehicleDescription}>{part.vehicle.description}</div>
            <div className={styles.tdgVehicleDetails}>
              <span className={styles.tdgDetailItem}>
                {part.vehicle.frontTireSize && part.vehicle.rearTireSize
                  ? <>F: {part.vehicle.frontTireSize}{'\u00A0\u00A0\u00A0'}R: {part.vehicle.rearTireSize}</>
                  : part.vehicle.tireSize && /Front:|F:/i.test(part.vehicle.tireSize)
                    ? part.vehicle.tireSize
                        .replace(/Front:\s*/i, 'F: ')
                        .replace(/[,/]\s*Rear:\s*/i, '\u00A0\u00A0\u00A0R: ')
                        .replace(/[,/]\s*R:\s*/i, '\u00A0\u00A0\u00A0R: ')
                    : <><span className={styles.tdgDetailLabel}>Size:</span> {part.vehicle.tireSize}</>}
              </span>
              <span className={styles.tdgDetailItem}>
                <span className={styles.tdgDetailLabel}>Season:</span> {getSeasonDisplay()}
              </span>
              {isOffRoadMode && (
                <span className={styles.tdgDetailItem}>
                  <span className={styles.tdgDetailLabel}>Off-Road:</span> Yes
                </span>
              )}
              {isRunflatMode && (
                <span className={styles.tdgDetailItem}>
                  <span className={styles.tdgDetailLabel}>Run-flat:</span> Yes
                </span>
              )}
            </div>
          </div>
        )}

        {/* Current Priorities */}
        {currentHighPriorities.length > 0 && (
          <div className={styles.tdgCurrentPriorities}>
            <div className={styles.tdgCurrentPrioritiesLabel}>Current Priorities:</div>
            <div className={styles.tdgCurrentPrioritiesList}>
              {currentHighPriorities.join(', ')}
            </div>
          </div>
        )}

        {/* Indicators (excluding offroad/staggered/runflat which are shown in metadata/toggle) */}
        {part.indicators && part.indicators.filter(i => i.type !== 'offroad' && i.type !== 'staggered' && i.type !== 'runflat').length > 0 && (
          <div className={styles.tdgIndicators}>
            {part.indicators.filter(i => i.type !== 'offroad' && i.type !== 'staggered' && i.type !== 'runflat').map((indicator, iIdx) => (
              <div key={iIdx} className={`${styles.tdgIndicator} ${styles[`indicator${indicator.type}`]}`}>
                <span className={styles.tdgIndicatorLabel}>{indicator.label}</span>
                {indicator.description && (
                  <span className={styles.tdgIndicatorDesc}>{indicator.description}</span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Original Recommendation Button */}
        <a
          href={addAnalyticsParam(part.tdgUrl)}
          target="_blank"
          rel="noopener noreferrer"
          className={`${styles.tdgCtaButton} ${hasModifications ? styles.tdgCtaButtonOriginalGrayed : ''}`}
          data-track-option="original"
          data-track-extra={JSON.stringify({ url: part.tdgUrl })}
        >
          Recommendations for customers like you
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </a>

        {/* Divider */}
        <div className={styles.tdgDivider} />

        {/* Customize Your Recommendation Section */}
        <div className={styles.tdgCustomizeSection}>
          <div className={styles.tdgCustomizeHeading}>Customize Your Recommendation:</div>

          {/* Priority Chips */}
          <div className={styles.tdgChipSection}>
            <div className={styles.tdgChipLabel}>Select Priorities (click to toggle):</div>
            <div
              className={styles.tdgChipList}
              data-track-id="tdg_chip"
              data-track-turn={turnNumber}
            >
              {visibleAttributes.map(attr => {
                const value = getPriorityValue(attr);
                const isEnabled = value >= 3; // 4 is enabled, 0 is disabled
                const chipClass = isEnabled ? styles.tdgChipHigh : styles.tdgChipNeutral;
                // Post-click value — matches the pre-refactor payload,
                // where `newValue` was computed at click time.
                const nextValue = togglePriorityValue(value);

                return (
                  <button
                    key={attr}
                    type="button"
                    disabled={!isInteractive}
                    className={`${styles.tdgChip} ${chipClass} ${!isInteractive ? styles.tdgChipDisabled : ''}`}
                    onClick={() => handleChipClick(attr)}
                    data-track-option={attr}
                    data-track-extra={JSON.stringify({ newValue: nextValue })}
                  >
                    {attributeLabels[attr]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tire Type Chips - Radio style for season type */}
          <div className={styles.tdgChipSection}>
            <div className={styles.tdgChipLabel}>Tire Type:</div>
            <div
              className={styles.tdgChipList}
              data-track-id="tdg_season"
              data-track-turn={turnNumber}
            >
              <button
                type="button"
                disabled={!isInteractive}
                className={`${styles.tdgChip} ${seasonType === 'allyear' ? styles.tdgChipHigh : styles.tdgChipNeutral} ${!isInteractive ? styles.tdgChipDisabled : ''}`}
                onClick={() => handleSeasonTypeClick('allyear')}
                data-track-option="allyear"
                data-track-extra={JSON.stringify({ previousSeason: seasonType })}
              >
                All-Season
              </button>
              <button
                type="button"
                disabled={!isInteractive}
                className={`${styles.tdgChip} ${seasonType === 'summerOnly' ? styles.tdgChipHigh : styles.tdgChipNeutral} ${!isInteractive ? styles.tdgChipDisabled : ''}`}
                onClick={() => handleSeasonTypeClick('summerOnly')}
                data-track-option="summerOnly"
                data-track-extra={JSON.stringify({ previousSeason: seasonType })}
              >
                Summer Only
              </button>
              <button
                type="button"
                disabled={!isInteractive}
                className={`${styles.tdgChip} ${seasonType === 'winterOnly' ? styles.tdgChipHigh : styles.tdgChipNeutral} ${!isInteractive ? styles.tdgChipDisabled : ''}`}
                onClick={() => handleSeasonTypeClick('winterOnly')}
                data-track-option="winterOnly"
                data-track-extra={JSON.stringify({ previousSeason: seasonType })}
              >
                Winter Only
              </button>
            </div>
          </div>

          {/* Off-Road Toggle - Only shown for off-road eligible vehicles (TRAC-181) */}
          {part.vehicle?.isOffRoad && (
            <div className={styles.tdgChipSection}>
              <div className={styles.tdgChipLabel}>Off-Road:</div>
              <div
                className={styles.tdgToggleContainer}
                data-track-id="tdg_offroad"
                data-track-turn={turnNumber}
              >
                <button
                  type="button"
                  disabled={!isInteractive}
                  className={`${styles.tdgToggleSwitch} ${isOffRoadMode ? styles.tdgToggleSwitchActive : ''}`}
                  onClick={() => handleOffRoadToggle()}
                  aria-label="Toggle off-road mode"
                  data-track-option={isOffRoadMode ? 'off' : 'on'}
                  data-track-extra={JSON.stringify({
                    previousValue: isOffRoadMode ? 'on' : 'off',
                  })}
                >
                  <div className={styles.tdgToggleSlider}></div>
                </button>
                <span className={styles.tdgToggleLabel}>{isOffRoadMode ? 'Yes' : 'No'}</span>
              </div>
            </div>
          )}

          {/* Runflat Toggle - Only shown for runflat eligible vehicles (TRAC-285) */}
          {part.vehicle?.isRunflat && (
            <div className={styles.tdgChipSection}>
              <div className={styles.tdgChipLabel}>Run-flat:</div>
              <div
                className={styles.tdgToggleContainer}
                data-track-id="tdg_runflat"
                data-track-turn={turnNumber}
              >
                <button
                  type="button"
                  disabled={!isInteractive}
                  className={`${styles.tdgToggleSwitch} ${isRunflatMode ? styles.tdgToggleSwitchActive : ''}`}
                  onClick={() => handleRunflatToggle()}
                  aria-label="Toggle runflat mode"
                  data-track-option={isRunflatMode ? 'off' : 'on'}
                  data-track-extra={JSON.stringify({
                    previousValue: isRunflatMode ? 'on' : 'off',
                  })}
                >
                  <div className={styles.tdgToggleSlider}></div>
                </button>
                <span className={styles.tdgToggleLabel}>{isRunflatMode ? 'Yes' : 'No'}</span>
              </div>
              {!isRunflatMode && (
                <div className={styles.tdgToggleWarning}>
                  Selecting Run-flat: No means I'm choosing non-run-flats and I may not have a spare.
                </div>
              )}
            </div>
          )}

          {/* Customized Recommendations Button */}
          {hasModifications ? (
            <a
              href={addAnalyticsParam(customizedUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.tdgCtaButton}
              data-track-option="customized"
              data-track-extra={JSON.stringify({ url: customizedUrl })}
            >
              Customized Recommendations
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </a>
          ) : (
            // Disabled variant — ships the attribute so the tdg_link
            // impression's optionsShown still lists both CTAs even before
            // the user touches anything (matches pre-refactor behavior).
            <button
              disabled
              className={styles.tdgCtaButton}
              data-track-option="customized"
            >
              Customized Recommendations
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TDGLinkCard;
